import { Box, Button, Grid, Typography, Container, Card, Chip, IconButton, Stack, Grid2 } from "@mui/material"
import { useEffect, useState } from "react";
import { getProjectsList, getProjectLookup, LookupsType } from '../../../utils/mergeData';
import { useNavigate } from "react-router-dom";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SiteImgIcon from '../../../resources/images/loc.png';
import CalendarIcon from '../../../resources/images/calendar.png';
import SelectComponent from "../../common/selectComponent";

import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HearingIcon from '@mui/icons-material/Hearing';
import SensorsIcon from '@mui/icons-material/Sensors';
import BiotechIcon from '@mui/icons-material/Biotech';
import Tooltip from '@mui/material/Tooltip';
import { getAnalysisTypesForProject } from '../../../utils/mapLayerUtils';

const SENSOR_ICON_CONFIG: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  'Camera': { color: '#e53935', label: 'Camera', icon: <CameraAltIcon sx={{ fontSize: '15px !important', color: '#e53935 !important' }} /> },
  'BioAcoustic': { color: '#8e24aa', label: 'BioAcoustic', icon: <HearingIcon sx={{ fontSize: '15px !important', color: '#8e24aa !important' }} /> },
  'Soil Sensor': { color: '#fb8c00', label: 'Soil Sensor', icon: <SensorsIcon sx={{ fontSize: '15px !important', color: '#fb8c00 !important' }} /> },
  'eDNA': { color: '#1976d2', label: 'eDNA', icon: <BiotechIcon sx={{ fontSize: '15px !important', color: '#1976d2 !important' }} /> },
};

const ProjectCard = ({
  name,
  status,
  startDate,
  endDate,
  types,
  sensorTypes,
  sites,
  onClick
}: any) => {
  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '340px',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)'
        }
      }}
      onClick={onClick}
    >
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#151E15',
            mb: 1.5,
            minHeight: '48px',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {name}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Chip
            label={status}
            sx={{
              backgroundColor: '#FFB800',
              color: '#151E15',
              fontWeight: 600,
              fontSize: '12px',
              height: '24px',
              borderRadius: '12px'
            }}
          />
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Box component="img" src={CalendarIcon} sx={{ width: 18, height: 18 }} />
          <Typography sx={{ fontSize: '13px', color: '#666666' }}>
            {startDate} - {endDate}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2, minHeight: '28px', flexWrap: 'wrap', gap: '4px' }}>
          {types.map((type: string, index: number) => (
            <Chip
              key={index}
              label={type}
              variant="outlined"
              sx={{
                borderColor: '#00A3FF',
                color: '#00A3FF',
                height: '28px',
                '& .MuiChip-label': { px: 1, fontSize: '12px', fontWeight: 500 }
              }}
            />
          ))}
        </Stack>

        {/* Analysis Types Indicators */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', minHeight: '44px', alignContent: 'flex-start', rowGap: 0.8 }}>
          {(sensorTypes || []).map((sensor: string) => {
            const config = SENSOR_ICON_CONFIG[sensor] || { color: '#666', label: sensor, icon: null };
            return (
              <Tooltip key={sensor} title={config.label} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {config.icon}
                  <Typography sx={{ fontSize: '12px', color: '#151E15', fontWeight: 500 }}>
                    {config.label}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 'auto', pt: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box component="img" src={SiteImgIcon} sx={{ width: 18, height: 18 }} />
          <Typography sx={{ fontSize: '14px', color: '#151E15', fontWeight: 500 }}>
            {sites} Sites
          </Typography>
        </Stack>

        <IconButton size="small" sx={{ color: '#FF0000' }} onClick={(e) => { e.stopPropagation(); }}>
          <DeleteOutlineIcon />
        </IconButton>
      </Stack>
    </Card>
  );
};

const ProjectComponent = () => {
  const navigate = useNavigate();
  const projectList = getProjectsList();
  const lookups: LookupsType = getProjectLookup();

  const [filteredProjects, setFilteredProjects] = useState(projectList);

  const [projectName] = useState(lookups.ProjectNamesArray);
  const [projectType] = useState(lookups.ProjectTypeArray);
  const [projectCountry] = useState(lookups.CountryNamesArray);
  const [projectRegistry] = useState(lookups.registryNamesArray);

  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedProjectRegistry, setSelectedProjectRegistry] = useState<number | null>(null);
  const [selectedProjectCountry, setSelectedProjectCountry] = useState<number | null>(null);
  const [selectedProjectType, setSelectedProjectType] = useState<number | null>(null);
  const handleFilter = () => {
    const filtered = projectList.filter((project) => {
      const matchesRegistry = !selectedProjectRegistry || project.registry === projectRegistry.find((r) => r.value === selectedProjectRegistry)?.label;
      const matchesCountry = !selectedProjectCountry || project.country === projectCountry.find((c) => c.value === selectedProjectCountry)?.label;
      const matchesType = !selectedProjectType || project.project_status === projectType.find((t) => t.value === selectedProjectType)?.label;
      const matchesName = !selectedProject || project.project_name === projectName.find((p) => p.value === selectedProject)?.label;

      return matchesRegistry && matchesCountry && matchesType && matchesName;
    });
    setFilteredProjects(filtered);
  };

  const handleReset = () => {
    setSelectedProject(null);
    setSelectedProjectRegistry(null);
    setSelectedProjectCountry(null);
    setSelectedProjectType(null);
    setFilteredProjects(projectList);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Hero Section */}
      {/* <Box 
        sx={{ 
          background: 'linear-gradient(90deg, #151E15 0%, #2D4340 100%)', 
          color: '#fff', 
          pt: 6, 
          pb: 6, 
          textAlign: 'center',
          width: '100%',
          mb: 4
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 1.5,
              fontSize: { xs: '1.5rem', md: '2.5rem' }
            }}
          >
            Welcome to Darukaa Earth
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 400, 
              opacity: 0.9,
              fontSize: { xs: '0.9rem', md: '1.1rem' }
            }}
          >
            Your central hub for managing Carbon, Biodiversity, and Climate projects.
          </Typography>
        </Container>
      </Box> */}

      <Container maxWidth="lg">
        {/* Modern Filter Section */}
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '20px',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.04)',
            p: 3,
            mb: 6,
            mt: -2,
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#666', fontWeight: 600, letterSpacing: '0.5px' }}>
            FILTER PROJECTS
          </Typography>
          <Grid2 container spacing={3} alignItems="flex-end">
            <Grid2 size={{ lg: 3, md: 6, xs: 12 }}>
              <SelectComponent
                label={"Project"}
                options={projectName}
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                displayEmpty={true}
                renderValue={(value) =>
                  value ? projectName.find((p) => p.value === value)?.label : "All Projects"
                }
              />
            </Grid2>
            <Grid2 size={{ lg: 3, md: 6, xs: 12 }}>
              <SelectComponent label={"Registry Name"} options={projectRegistry} value={selectedProjectRegistry} onChange={(e) => setSelectedProjectRegistry(e.target.value)} />
            </Grid2>
            <Grid2 size={{ lg: 3, md: 6, xs: 12 }}>
              <SelectComponent label={"Country"} options={projectCountry} value={selectedProjectCountry} onChange={(e) => setSelectedProjectCountry(e.target.value)} />
            </Grid2>
            <Grid2 size={{ lg: 3, md: 6, xs: 12 }}>
              <SelectComponent label={"Project Type"} options={projectType} value={selectedProjectType} onChange={(e) => setSelectedProjectType(e.target.value)} />
            </Grid2>

            {/* Buttons on a new line */}
            <Grid2 size={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1, pt: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
              <Button
                variant="text"
                sx={{
                  color: '#666',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'transparent', color: '#005F54' }
                }}
                onClick={handleReset}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#005F54",
                  color: "#fff",
                  borderRadius: '12px',
                  px: 4,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#004d44', boxShadow: 'none' }
                }}
                onClick={handleFilter}
              >
                Apply
              </Button>
            </Grid2>
          </Grid2>
        </Box>

        {/* Project Grid */}
        <Box sx={{ pb: 8 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#151E15' }}>
              Explore Projects
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Showing {filteredProjects.length} results
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {filteredProjects.map((project: any, index: number) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                <ProjectCard
                  name={project.project_name}
                  status={project.project_status}
                  startDate={project.project_start_date}
                  endDate={project.project_end_date}
                  types={project.project_type ? project.project_type.split('/') : ['Biodiversity']}
                  sensorTypes={getAnalysisTypesForProject(project.project_name)}
                  sites={project.number_of_sites || '0'}
                  onClick={() => {
                    localStorage.setItem('selectedProject', JSON.stringify(project));
                    navigate(`/project-details/${project.project_name.replace(/\s+/g, '-')}`);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}

export default ProjectComponent